import { TILE_SIZE } from '@sokdaq/protocol';
import { tileAt, type MapObject, type OfficeMap, type TileKind } from '@sokdaq/room-core';
import { PALETTE, tileNoise } from './palette.js';

/**
 * 타일과 오브젝트를 그린다.
 *
 * 좌표계는 타일 픽셀 단위(1타일 = TILE_SIZE)다. 호출부가 카메라 변환을
 * 걸어둔 상태로 부르므로 여기서는 맵 절대 좌표만 신경 쓰면 된다.
 */

const T = TILE_SIZE;

export function drawTile(
  ctx: CanvasRenderingContext2D,
  map: OfficeMap,
  x: number,
  y: number,
): void {
  const px = x * T;
  const py = y * T;
  const kind = tileAt(map, x, y);

  switch (kind) {
    case 'wall':
      drawWall(ctx, map, x, y, px, py);
      return;
    case 'carpet':
      drawCarpet(ctx, x, y, px, py);
      return;
    case 'door':
      drawDoor(ctx, px, py);
      return;
    case 'desk':
      drawFloor(ctx, x, y, px, py);
      drawDesk(ctx, x, y, px, py);
      return;
    case 'plant':
      drawFloor(ctx, x, y, px, py);
      drawPlant(ctx, px, py);
      return;
    case 'floor':
      drawFloor(ctx, x, y, px, py);
      return;
  }
}

function drawFloor(ctx: CanvasRenderingContext2D, x: number, y: number, px: number, py: number): void {
  ctx.fillStyle = PALETTE.floor;
  ctx.fillRect(px, py, T, T);

  // 타일 이음매. 격자가 보여야 거리감이 생긴다.
  ctx.fillStyle = PALETTE.floorSeam;
  ctx.fillRect(px, py + T - 1, T, 1);
  ctx.fillRect(px + T - 1, py, 1, T);

  const noise = tileNoise(x, y);
  if (noise > 0.88) {
    ctx.fillStyle = PALETTE.floorSpeck;
    const sx = px + 6 + Math.floor(noise * 17);
    const sy = py + 6 + Math.floor(tileNoise(y, x) * 17);
    ctx.fillRect(sx, sy, 2, 2);
  }
}

function drawCarpet(ctx: CanvasRenderingContext2D, x: number, y: number, px: number, py: number): void {
  ctx.fillStyle = PALETTE.carpet;
  ctx.fillRect(px, py, T, T);

  // 성긴 격자 무늬로 바닥과 구분한다. 구역 안에 있다는 걸 색만으로
  // 알기 어려워서 질감을 더한다.
  ctx.fillStyle = PALETTE.carpetWeave;
  for (let i = 0; i < T; i += 8) {
    ctx.fillRect(px + i, py, 1, T);
    ctx.fillRect(px, py + i, T, 1);
  }
  void tileNoise(x, y);
}

function drawWall(
  ctx: CanvasRenderingContext2D,
  map: OfficeMap,
  x: number,
  y: number,
  px: number,
  py: number,
): void {
  ctx.fillStyle = PALETTE.wall;
  ctx.fillRect(px, py, T, T);

  // 아래가 벽이 아니면 그 면은 우리 눈에 보이는 윗면이다.
  if (tileAt(map, x, y + 1) !== 'wall') {
    ctx.fillStyle = PALETTE.wallTop;
    ctx.fillRect(px, py + T - 6, T, 6);
  }
}

function drawDoor(ctx: CanvasRenderingContext2D, px: number, py: number): void {
  ctx.fillStyle = PALETTE.doorPanel;
  ctx.fillRect(px, py, T, T);
  ctx.fillStyle = PALETTE.doorFrame;
  ctx.fillRect(px, py, 3, T);
  ctx.fillRect(px + T - 3, py, 3, T);
}

function drawDesk(ctx: CanvasRenderingContext2D, x: number, y: number, px: number, py: number): void {
  ctx.fillStyle = PALETTE.deskEdge;
  ctx.fillRect(px + 1, py + 6, T - 2, T - 8);
  ctx.fillStyle = PALETTE.deskTop;
  ctx.fillRect(px + 1, py + 6, T - 2, T - 12);

  // 책상마다 모니터를 놓으면 화면이 시끄럽다. 절반쯤만.
  if (tileNoise(x, y) > 0.5) {
    ctx.fillStyle = PALETTE.monitor;
    ctx.fillRect(px + 8, py + 2, 16, 12);
    ctx.fillStyle = PALETTE.monitorGlow;
    ctx.fillRect(px + 10, py + 4, 12, 8);
    ctx.fillStyle = PALETTE.monitor;
    ctx.fillRect(px + 14, py + 14, 4, 2);
  } else {
    ctx.fillStyle = PALETTE.keyboard;
    ctx.fillRect(px + 8, py + 12, 16, 5);
  }
}

function drawPlant(ctx: CanvasRenderingContext2D, px: number, py: number): void {
  ctx.fillStyle = PALETTE.plantPot;
  ctx.fillRect(px + 11, py + 20, 10, 9);

  ctx.fillStyle = PALETTE.plantLeafDark;
  ctx.fillRect(px + 8, py + 8, 16, 12);
  ctx.fillStyle = PALETTE.plantLeaf;
  ctx.fillRect(px + 10, py + 5, 12, 12);
  ctx.fillRect(px + 14, py + 2, 4, 6);
}

export function drawObject(ctx: CanvasRenderingContext2D, object: MapObject): void {
  const px = object.x * T;
  const py = object.y * T;

  // 바닥 그림자. 오브젝트가 타일 위에 얹혀 있다는 느낌을 준다.
  ctx.fillStyle = PALETTE.shadow;
  ctx.fillRect(px + 5, py + T - 6, T - 10, 4);

  switch (object.id) {
    case 'water-cooler':
      ctx.fillStyle = PALETTE.coolerBody;
      ctx.fillRect(px + 10, py + 12, 12, 16);
      ctx.fillStyle = PALETTE.cooler;
      ctx.fillRect(px + 11, py + 2, 10, 11);
      ctx.fillStyle = PALETTE.monitor;
      ctx.fillRect(px + 14, py + 18, 4, 3);
      return;

    case 'coffee':
      ctx.fillStyle = PALETTE.coffeeBody;
      ctx.fillRect(px + 7, py + 6, 18, 22);
      ctx.fillStyle = PALETTE.monitorGlow;
      ctx.fillRect(px + 10, py + 9, 12, 4);
      ctx.fillStyle = PALETTE.coffeeCup;
      ctx.fillRect(px + 13, py + 19, 6, 5);
      return;

    case 'printer':
      ctx.fillStyle = PALETTE.printerBody;
      ctx.fillRect(px + 5, py + 12, 22, 16);
      ctx.fillStyle = PALETTE.printerPaper;
      ctx.fillRect(px + 9, py + 6, 14, 8);
      return;

    case 'whiteboard':
      ctx.fillStyle = PALETTE.boardFrame;
      ctx.fillRect(px + 2, py + 4, T - 4, 22);
      ctx.fillStyle = PALETTE.boardFace;
      ctx.fillRect(px + 4, py + 6, T - 8, 18);
      ctx.fillStyle = PALETTE.boardInk;
      ctx.fillRect(px + 7, py + 10, 14, 2);
      ctx.fillRect(px + 7, py + 15, 9, 2);
      return;

    default:
      // 맵에 오브젝트를 추가하고 그리기를 잊었을 때. 안 보이는 것보다
      // 눈에 띄는 상자로 나오는 편이 낫다.
      ctx.fillStyle = PALETTE.printerBody;
      ctx.fillRect(px + 6, py + 6, T - 12, T - 12);
  }
}

/** 화면에 걸치는 타일 범위만 그린다. 맵 전체를 매 프레임 도는 건 낭비다. */
export function visibleTileRange(
  map: OfficeMap,
  cameraTileX: number,
  cameraTileY: number,
  viewTilesX: number,
  viewTilesY: number,
): { x0: number; y0: number; x1: number; y1: number } {
  return {
    x0: Math.max(0, Math.floor(cameraTileX) - 1),
    y0: Math.max(0, Math.floor(cameraTileY) - 1),
    x1: Math.min(map.width - 1, Math.ceil(cameraTileX + viewTilesX) + 1),
    y1: Math.min(map.height - 1, Math.ceil(cameraTileY + viewTilesY) + 1),
  };
}

export type { TileKind };
